import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { useDailyDamages } from '@/hooks/useInventory';
import { DAMAGE_TYPE_LABELS, type DamageType } from '@/types/database/inventory';
import { format } from 'date-fns';
import { DateNavigator } from './DateNavigator';
import { formatCurrency } from '@/lib/utils';

const damageTypeBadgeVariants: Record<DamageType, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  expired: 'destructive',
  spoiled: 'destructive',
  broken: 'outline',
  other: 'secondary',
};

export function DamagesTable() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const { data: damages, isLoading } = useDailyDamages({
    damageType: typeFilter !== 'all' ? typeFilter : undefined,
    startDate: dateFilter,
    endDate: dateFilter,
  });

  const filteredDamages = damages?.filter((d) =>
    d.product?.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.reason?.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate daily totals
  const totalQuantity = filteredDamages?.reduce((sum, d) => sum + d.quantity, 0) || 0;
  const totalValue = filteredDamages?.reduce((sum, d) => sum + (d.damage_value || 0), 0) || 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <DateNavigator date={dateFilter} onChange={setDateFilter} />
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search damages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Damage type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(DAMAGE_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Daily Summary */}
      {filteredDamages && filteredDamages.length > 0 && (
        <div className="flex gap-4 text-sm">
          <div className="bg-muted/50 px-3 py-1.5 rounded-md">
            <span className="text-muted-foreground">Total Qty:</span>{' '}
            <span className="font-mono font-medium text-destructive">-{totalQuantity.toLocaleString()} kg</span>
          </div>
          <div className="bg-muted/50 px-3 py-1.5 rounded-md">
            <span className="text-muted-foreground">Total Value:</span>{' '}
            <span className="font-mono font-medium text-destructive">{formatCurrency(totalValue)}</span>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Value (FIFO)</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDamages?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No damage records found
                </TableCell>
              </TableRow>
            ) : (
              filteredDamages?.map((damage) => (
                <TableRow key={damage.id}>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {format(new Date(damage.damage_date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="font-medium">
                    {damage.product?.name || 'Unknown Product'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={damageTypeBadgeVariants[damage.damage_type]}>
                      {DAMAGE_TYPE_LABELS[damage.damage_type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-destructive">
                    -{damage.quantity.toLocaleString()} kg
                  </TableCell>
                  <TableCell className="text-right font-mono text-destructive">
                    {damage.damage_value ? formatCurrency(damage.damage_value) : '-'}
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate text-muted-foreground">
                    {damage.reason || '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
